import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Loader2,
    Download,
    BarChart3,
    ClipboardList,
    AlertTriangle,
    ShieldAlert,
    Users,
    UserCheck,
    Package,
    MapPin,
    GraduationCap,
    Calendar,
    RefreshCw,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';

const API = '/api/calidad/sura-reporta';

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Fechas locales en YYYY-MM-DD (toISOString es UTC y corre el dia de noche).
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const RANGOS = [
    { id: 'mes', label: 'Este mes' },
    { id: '3m', label: '3 meses' },
    { id: '6m', label: '6 meses' },
    { id: 'anio', label: 'Este año' },
];

const rangoDe = (id) => {
    const hoy = new Date();
    const hasta = isoLocal(hoy);
    if (id === 'mes') return { desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta };
    if (id === '3m') return { desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)), hasta };
    if (id === 'anio') return { desde: isoLocal(new Date(hoy.getFullYear(), 0, 1)), hasta };
    return { desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 5, 1)), hasta };
};

// Semanas (lunes a domingo) del mes de `desdeIso`, recortadas a los dias del mes:
// [{ semana: 'YYYY-MM-DD' (lunes), nombre: '5–11 oct' }]. Incluye las que aun no
// pasaron, para que el grafico muestre el mes completo.
const semanasDelMes = (desdeIso) => {
    const [a, m] = desdeIso.split('-').map(Number);
    const primero = new Date(a, m - 1, 1);
    const ultimo = new Date(a, m, 0);
    const lunes = new Date(primero);
    lunes.setDate(primero.getDate() - ((primero.getDay() + 6) % 7));
    const semanas = [];
    while (lunes <= ultimo) {
        const domingo = new Date(lunes);
        domingo.setDate(lunes.getDate() + 6);
        const ini = lunes < primero ? primero : lunes;
        const fin = domingo > ultimo ? ultimo : domingo;
        semanas.push({
            semana: isoLocal(lunes),
            nombre: ini.getDate() === fin.getDate()
                ? `${ini.getDate()} ${MESES_CORTOS[m - 1]}`
                : `${ini.getDate()}–${fin.getDate()} ${MESES_CORTOS[m - 1]}`,
        });
        lunes.setDate(lunes.getDate() + 7);
    }
    return semanas;
};

const etiquetaMes = (mes) => {
    const [a, m] = (mes || '').split('-').map(Number);
    return a && m ? `${MESES_CORTOS[m - 1]} ${String(a).slice(2)}` : mes;
};

const descargarBlob = (data, nombre) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    a.click();
    window.URL.revokeObjectURL(url);
};

const Kpi = ({ icon: Icon, color, valor, label }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color.bg}`}>
                <Icon size={20} className={color.text} />
            </div>
            <div>
                <p className="text-2xl font-bold text-gray-900">{valor ?? 0}</p>
                <p className="text-xs text-gray-500">{label}</p>
            </div>
        </div>
    </div>
);

// Lista con barra proporcional, mismo estilo que los reportes de taxis y papeleria.
const BloqueBarras = ({ icon: Icon, iconColor, titulo, items, barColor = 'bg-[#0062A5]', max = 8, vacio = 'Sin datos en este período' }) => {
    const lista = (items || []).filter(i => (i.total || 0) > 0).slice(0, max);
    const mayor = Math.max(1, ...lista.map(i => i.total || 0));
    const suma = (items || []).reduce((t, i) => t + (i.total || 0), 0);
    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-2">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Icon size={16} className={iconColor} />
                    {titulo}
                </h3>
                {suma > 0 && <span className="text-xs text-gray-400">{suma} en total</span>}
            </div>
            {lista.length === 0 ? (
                <p className="px-4 py-8 text-sm text-gray-400 text-center">{vacio}</p>
            ) : (
                <div className="p-4 space-y-3">
                    {lista.map((item) => {
                        const pct = suma ? Math.round(((item.total || 0) / suma) * 100) : 0;
                        return (
                            <div key={item.id ?? item.label}>
                                <div className="flex items-center justify-between gap-3 mb-1">
                                    <span className="text-sm text-gray-700 truncate">{item.label}</span>
                                    <span className="text-xs text-gray-500 shrink-0">
                                        <span className="font-semibold text-gray-900">{item.total}</span> · {pct}%
                                    </span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${((item.total || 0) / mayor) * 100}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const CalidadDashboard = () => {
    const [rango, setRango] = useState('mes');
    const [fechas, setFechas] = useState(() => rangoDe('mes'));
    const [datos, setDatos] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exportando, setExportando] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(`${API}/dashboard`, { params: fechas });
            setDatos(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo cargar el dashboard');
        } finally {
            setLoading(false);
        }
    }, [fechas]);

    useEffect(() => { cargar(); }, [cargar]);

    const elegirRango = (id) => {
        setRango(id);
        setFechas(rangoDe(id));
    };

    const cambiarFecha = (campo, valor) => {
        setRango('custom');
        setFechas(prev => ({ ...prev, [campo]: valor }));
    };

    const exportar = async () => {
        setExportando(true);
        try {
            const res = await axiosInstance.get(`${API}/reportes/excel`, { params: fechas, responseType: 'blob' });
            descargarBlob(res.data, `Sura_Reporta_${fechas.desde}_${fechas.hasta}.xlsx`);
        } catch {
            toast.error('No se pudo exportar el Excel');
        } finally {
            setExportando(false);
        }
    };

    // Un solo mes (p.ej. "Este mes"): por semana. Varios meses: por mes.
    const porSemana = Boolean(fechas.desde && fechas.hasta && fechas.desde.slice(0, 7) === fechas.hasta.slice(0, 7));
    const porMes = useMemo(() => {
        if (porSemana) {
            const conteo = new Map((datos?.por_semana || []).map(w => [w.semana, w]));
            const semanas = semanasDelMes(fechas.desde);
            // Sin ningun diagnostico en el mes, el grafico muestra "Sin datos".
            if (!semanas.some(w => conteo.get(w.semana)?.diagnosticos)) return [];
            return semanas.map(w => ({
                nombre: w.nombre,
                diagnosticos: conteo.get(w.semana)?.diagnosticos || 0,
                con_falla: conteo.get(w.semana)?.con_falla || 0,
                criticos: conteo.get(w.semana)?.criticos || 0,
            }));
        }
        return (datos?.por_mes || []).map(m => ({ ...m, nombre: etiquetaMes(m.mes) }));
    }, [datos, porSemana, fechas.desde]);
    const totales = datos?.totales || {};
    const sinDatos = !loading && datos && !totales.diagnosticos && !totales.conexiones;

    return (
        <div className="space-y-6">
            {/* Controles */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex bg-gray-100 p-1 rounded-xl">
                    {RANGOS.map(r => (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => elegirRango(r.id)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${rango === r.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    <input
                        type="date"
                        value={fechas.desde}
                        max={fechas.hasta}
                        onChange={e => cambiarFecha('desde', e.target.value)}
                        className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                    />
                    <span className="text-gray-400 text-sm">a</span>
                    <input
                        type="date"
                        value={fechas.hasta}
                        min={fechas.desde}
                        onChange={e => cambiarFecha('hasta', e.target.value)}
                        className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                    />
                </div>
                <button
                    onClick={exportar}
                    disabled={exportando || !totales.diagnosticos}
                    className="ml-auto flex items-center gap-2 px-4 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                    {exportando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    Exportar Excel
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={32} className="animate-spin text-gray-400" />
                </div>
            ) : error ? (
                <div className="text-center py-16">
                    <p className="text-gray-500">{error}</p>
                    <button onClick={cargar} className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-sura-blue hover:underline">
                        <RefreshCw size={14} /> Reintentar
                    </button>
                </div>
            ) : sinDatos ? (
                <div className="text-center py-20">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BarChart3 size={32} className="text-gray-300" />
                    </div>
                    <p className="text-gray-500 text-lg">Sin diagnósticos en este período</p>
                    <p className="text-gray-400 text-sm mt-1">Cuando los colaboradores usen Sura Reporta, sus datos aparecerán aquí</p>
                </div>
            ) : (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                        <Kpi icon={ClipboardList} color={{ bg: 'bg-blue-50', text: 'text-[#0062A5]' }} valor={totales.diagnosticos} label="Diagnósticos" />
                        <Kpi icon={AlertTriangle} color={{ bg: 'bg-blue-50', text: 'text-[#0062A5]' }} valor={totales.con_falla} label="Reportes con falla" />
                        <Kpi icon={ShieldAlert} color={{ bg: 'bg-red-50', text: 'text-red-600' }} valor={totales.criticos} label="Críticos" />
                        <Kpi icon={Users} color={{ bg: 'bg-blue-50', text: 'text-[#0062A5]' }} valor={totales.participantes} label="Participantes" />
                        <Kpi icon={UserCheck} color={{ bg: 'bg-blue-50', text: 'text-[#0062A5]' }} valor={totales.conexiones} label="Colaboradores conectados" />
                    </div>

                    {/* Reportes por mes (o por semana si se mira un solo mes) */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <BarChart3 size={16} className="text-gray-400" />
                            {porSemana ? 'Reportes por semana' : 'Reportes por mes'}
                        </h3>
                        {porMes.length === 0 ? (
                            <p className="py-8 text-sm text-gray-400 text-center">Sin datos en este período</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={porMes} barGap={2} margin={{ left: -10, right: 10 }} style={{ outline: 'none' }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                    <XAxis dataKey="nombre" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                                    <RechartsTooltip cursor={{ fill: '#f3f4f6' }} />
                                    <Legend formatter={v => <span className="text-xs font-medium text-gray-600 ml-1">{v}</span>} iconType="square" wrapperStyle={{ paddingTop: '10px' }} />
                                    <Bar dataKey="diagnosticos" name="Diagnósticos" fill="#003366" radius={[3, 3, 0, 0]} />
                                    <Bar dataKey="con_falla" name="Con falla" fill="#6B9BC9" radius={[3, 3, 0, 0]} />
                                    <Bar dataKey="criticos" name="Críticos" fill="#dc2626" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Consolidado de fallas. Lo de la encuesta de conexion esta en su pestaña. */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <BloqueBarras icon={Package} iconColor="text-gray-400" titulo="Por línea de producto" items={datos?.por_linea} />
                        <BloqueBarras icon={AlertTriangle} iconColor="text-gray-400" titulo="Por tipo de falla" items={datos?.por_desviacion} />
                        <BloqueBarras icon={MapPin} iconColor="text-gray-400" titulo="Por región" items={datos?.por_region} max={10} />
                        <BloqueBarras icon={GraduationCap} iconColor="text-gray-400" titulo="Interés en cápsulas" items={datos?.capsulas} />
                    </div>
                </>
            )}
        </div>
    );
};

export default CalidadDashboard;
